/**
 * Collects the player's game files from what they dropped or chose: disc images, loose MPQs,
 * the patch executables (D2Patch_114d.exe, LODPatch_109d.exe), or whole folders (a Diablo II
 * install folder from the digital download with its patch_d2.mpq and Game.exe, a copied CD, the
 * Blizzard installer folders with their Installer Tome.mpq). Only names are looked at here;
 * nothing is read.
 */
import { archiveNameOf, type ArchiveName, type IgnoredFile, ignoredReason, isInstallerTome } from '@d2/formats';

export interface PickedFile {
  /** Path relative to what was dropped ("Diablo II/d2data.mpq"), or the file name. */
  path: string;
  file: Blob;
}

export interface Picked {
  archives: { name: ArchiveName; file: Blob; origin: string }[];
  isos: PickedFile[];
  /** Blizzard installer tomes (`Installer Tome.mpq`), opened to find the archives inside. */
  tomes: PickedFile[];
  ignored: IgnoredFile[];
  /** Number of files that were not game files (Game.exe, saves, readme…). */
  skipped: number;
}

/** Sorts dropped files into archives, disc images and recognised-but-unused files. */
export function pickGameFiles(files: PickedFile[]): Picked {
  const out: Picked = { archives: [], isos: [], tomes: [], ignored: [], skipped: 0 };
  // Shallow paths first: a folder's own MPQs win over copies in backup sub-folders.
  const sorted = [...files].sort((a, b) => depth(a.path) - depth(b.path) || a.path.localeCompare(b.path));
  for (const f of sorted) {
    const name = f.path.split('/').pop()!;
    if (/\.iso$/i.test(name)) {
      out.isos.push(f);
      continue;
    }
    if (isInstallerTome(name)) {
      out.tomes.push(f);
      continue;
    }
    const a = archiveNameOf(name);
    if (a) {
      out.archives.push({ name: a, file: f.file, origin: f.path });
      continue;
    }
    const reason = ignoredReason(name);
    if (reason) out.ignored.push({ name: name.toLowerCase(), origin: f.path, reason });
    else out.skipped++;
  }
  return out;
}

const depth = (p: string) => p.split('/').length;

/** Folder levels searched below a dropped folder (Diablo II/, Diablo II/Patches/…). */
const MAX_DEPTH = 3;

interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}
interface FsFileEntry extends FsEntry {
  file(ok: (f: File) => void, err: (e: unknown) => void): void;
}
interface FsDirEntry extends FsEntry {
  createReader(): { readEntries(ok: (e: FsEntry[]) => void, err: (e: unknown) => void): void };
}

async function walk(entry: FsEntry, level: number, out: PickedFile[]): Promise<void> {
  if (entry.isFile) {
    // Only names we may use are opened.
    if (!/\.(iso|mpq|exe)$/i.test(entry.name)) {
      out.push({ path: entry.fullPath.replace(/^\//, ''), file: new Blob() });
      return;
    }
    const file = await new Promise<File>((ok, err) => (entry as FsFileEntry).file(ok, err));
    out.push({ path: entry.fullPath.replace(/^\//, ''), file });
    return;
  }
  if (!entry.isDirectory || level > MAX_DEPTH) return;
  const reader = (entry as FsDirEntry).createReader();
  for (;;) {
    const batch = await new Promise<FsEntry[]>((ok, err) => reader.readEntries(ok, err));
    if (!batch.length) break;
    for (const e of batch) await walk(e, level + 1, out);
  }
}

/** Files of a drop, descending into dropped folders. */
export async function filesFromDrop(dt: DataTransfer): Promise<PickedFile[]> {
  const out: PickedFile[] = [];
  const entries = [...dt.items]
    .map((i) => (i.kind === 'file' ? (i as unknown as { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry?.() : null))
    .filter((e): e is FsEntry => !!e);
  if (!entries.length) return [...dt.files].map((f) => ({ path: f.name, file: f }));
  for (const e of entries) await walk(e, 0, out);
  return out;
}

/** Files of an <input type=file> (with or without `webkitdirectory`). */
export function filesFromInput(input: HTMLInputElement): PickedFile[] {
  return [...(input.files ?? [])].map((f) => ({ path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name, file: f }));
}
